/**
 * 訪問履歴からのスタンプ提案バナー
 *
 * 自動では絶対にチェックしない。ユーザーが選んで承認したものだけを送る。
 * 行ったがスタンプを取れなかった訪問は却下でき、却下はサーバに残るので次回も出てこない。
 */

import { format } from 'date-fns'
import { ChevronDown, MapPin, X } from 'lucide-react'
import { useState } from 'react'

import type { VisitSuggestion } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

type Props = {
  suggestions: VisitSuggestion[]
  isSubmitting: boolean
  onApprove: (itemIds: number[]) => void
  onDismiss: (suggestion: VisitSuggestion) => void
}

const stayLabel = (suggestion: VisitSuggestion): string => {
  if (suggestion.stay_minutes === null) return '滞在時間不明'
  if (suggestion.stay_minutes < 60) return `${suggestion.stay_minutes}分滞在`
  return `${Math.floor(suggestion.stay_minutes / 60)}時間${suggestion.stay_minutes % 60}分滞在`
}

const matchLabel = (suggestion: VisitSuggestion): string => {
  if (suggestion.matched_by === 'place') return '場所が一致'
  return suggestion.distance_m === null ? '付近の記録' : `${Math.round(suggestion.distance_m)}m 以内`
}

export const VisitSuggestionBanner = ({ suggestions, isSubmitting, onApprove, onDismiss }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  // 初期は全選択。ユーザーが外したものだけを覚える
  const [excluded, setExcluded] = useState<Set<number>>(new Set())

  if (suggestions.length === 0) return null

  const selectedIds = suggestions.map((s) => s.item_id).filter((id) => !excluded.has(id))

  const toggle = (itemId: number) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-primary/30 bg-primary/5">
      <button
        type="button"
        className="flex w-full items-center gap-2 p-3 text-left"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <MapPin className="size-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 text-sm">
          <span className="font-medium">{suggestions.length} 件</span>
          のスポットに訪問した記録があります
        </span>
        <ChevronDown className={cn('size-4 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="border-t border-primary/20 px-3 pb-3">
          <ul className="divide-y divide-primary/10">
            {suggestions.map((suggestion) => (
              <li key={suggestion.item_id} className="flex items-center gap-3 py-2">
                <Checkbox
                  checked={!excluded.has(suggestion.item_id)}
                  onCheckedChange={() => toggle(suggestion.item_id)}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{suggestion.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {format(parseUtcDate(suggestion.visited_at), 'yyyy/M/d')}・{stayLabel(suggestion)}・
                    {matchLabel(suggestion)}
                    {suggestion.visit_count > 1 && `・${suggestion.visit_count}回`}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="この訪問ではスタンプを取れなかった"
                  title="この訪問ではスタンプを取れなかった"
                  disabled={isSubmitting}
                  onClick={() => onDismiss(suggestion)}
                  className="shrink-0 rounded-md p-1.5! text-muted-foreground hover:bg-primary/10 disabled:opacity-50"
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>

          <Button
            className="mt-3 w-full"
            disabled={isSubmitting || selectedIds.length === 0}
            onClick={() => onApprove(selectedIds)}
          >
            選択した {selectedIds.length} 件をチェック
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            × を押すとその訪問を候補から外します。同じ場所に別の訪問があれば次の記録が出ます。
          </p>
        </div>
      )}
    </div>
  )
}
